import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { Icon } from '../../../../components';
import { AFFILIATE_FORM, CASINO_SHEET } from '../../../../theme/tokens';
import { Backdrop, Panel, Header, HeaderCenter, Title, CloseBtn, Body } from './PreviewOverlay.styles';

export type PreviewOverlayProps = {
  /** Header title (i18n key). */
  title: string;
  onClose: () => void;
  /** Optional control centred in the header (e.g. the device pill). Hidden on mobile. */
  center?: React.ReactNode;
  children: React.ReactNode;
};

/**
 * A large content-area preview overlay: a "Preview" header (+ X) aligned to the top of the layout's
 * content card (the sidebar top), extending down to the viewport's bottom edge, with an edge-to-edge
 * body for the embedded website. PORTALLED to <body> so its fixed positioning is viewport-relative (a
 * transformed layout ancestor would otherwise re-root it) and it can pin to the content card via the
 * live --content-* vars. Slides up on open / down on close, and owns scroll-lock + Escape / backdrop
 * close. Distinct from the shared centred Sheet on purpose — this one is a full-bleed preview surface.
 */
export const PreviewOverlay = ({ title, onClose, center, children }: PreviewOverlayProps): React.ReactElement => {
  const { t } = useTranslation();
  const [closing, setClosing] = React.useState(false);
  const closedRef = React.useRef(false);
  const onCloseRef = React.useRef(onClose);
  onCloseRef.current = onClose;

  // Play the exit (slide down + fade), THEN unmount. Guarded so it fires once.
  const requestClose = React.useCallback((): void => {
    if (closedRef.current) return;
    closedRef.current = true;
    setClosing(true);
    window.setTimeout(() => onCloseRef.current(), CASINO_SHEET.CLOSE_MS);
  }, []);

  // Lock the background scroll while open + close on Escape.
  React.useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') requestClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previous;
      document.removeEventListener('keydown', onKey);
    };
  }, [requestClose]);

  return createPortal(
    <>
      <Backdrop $closing={closing} onClick={requestClose} />
      <Panel $closing={closing} role="dialog" aria-modal="true" aria-label={title}>
        <Header>
          <Title text={title} />
          {center ? <HeaderCenter>{center}</HeaderCenter> : null}
          <CloseBtn type="button" aria-label={t('Close')} onClick={requestClose}>
            <Icon name="close" size={18} color={AFFILIATE_FORM.closeGlyph} />
          </CloseBtn>
        </Header>
        <Body>{children}</Body>
      </Panel>
    </>,
    document.body,
  );
};

export default PreviewOverlay;
